import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingStatus } from '../../types';
import {
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
} from '../../services/push';

interface NotificationsState {
  pushEnabled: boolean;
  pushRegistered: boolean;
  permission: NotificationPermission | 'unsupported';
  status: LoadingStatus;
}

const initialState: NotificationsState = {
  pushEnabled: false,
  pushRegistered: false,
  permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
  status: 'idle',
};

/**
 * Register the service worker and auto-subscribe if permission was already granted.
 * Called once on authenticated app mount.
 */
export const initPushNotifications = createAsyncThunk(
  'notifications/init',
  async (_, { dispatch }) => {
    if (typeof Notification === 'undefined') {
      return { permission: 'unsupported' as const, pushEnabled: false };
    }

    const permission = Notification.permission;
    dispatch(setPermission(permission));

    const registration = await registerServiceWorker();
    if (!registration) return { permission, pushEnabled: false };

    if (permission === 'granted') {
      const sub = await subscribeToPush(registration);
      return { permission, pushEnabled: !!sub };
    }

    return { permission, pushEnabled: false };
  },
);

/**
 * Request push permission from the user and subscribe if granted.
 */
export const requestPushPermission = createAsyncThunk(
  'notifications/requestPermission',
  async () => {
    if (typeof Notification === 'undefined') {
      return { permission: 'unsupported' as const, pushEnabled: false };
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { permission, pushEnabled: false };
    }

    const registration = await registerServiceWorker();
    if (!registration) return { permission, pushEnabled: false };

    const sub = await subscribeToPush(registration);
    return { permission, pushEnabled: !!sub };
  },
);

/**
 * Unsubscribe from push notifications.
 */
export const disablePushNotifications = createAsyncThunk(
  'notifications/disable',
  async () => {
    await unsubscribeFromPush();
    return { pushEnabled: false };
  },
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setPermission(state, action: PayloadAction<NotificationPermission | 'unsupported'>) {
      state.permission = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initPushNotifications.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(initPushNotifications.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.pushRegistered = true;
        state.permission = action.payload.permission as NotificationPermission | 'unsupported';
        state.pushEnabled = action.payload.pushEnabled;
      })
      .addCase(initPushNotifications.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(requestPushPermission.fulfilled, (state, action) => {
        state.permission = action.payload.permission as NotificationPermission | 'unsupported';
        state.pushEnabled = action.payload.pushEnabled;
      })
      .addCase(disablePushNotifications.fulfilled, (state) => {
        state.pushEnabled = false;
      });
  },
});

export const { setPermission } = notificationsSlice.actions;
export default notificationsSlice.reducer;
